import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { AuthStore, User, AuthTokens, ErpConnection, ErpEmployeeIdByConnection, OfflineAuthSnapshot } from '../types'

const initialState = {
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  erpConnections: [] as ErpConnection[],
  selectedErpConnectionId: null as number | null,
  pendingErpSelection: false,
  erpEmployeeIdByConnection: {} as ErpEmployeeIdByConnection,
  offlineSession: false,
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setAuth: (user: User, tokens: AuthTokens) => {
        set({
          user,
          tokens,
          isAuthenticated: true,
          isLoading: false,
          offlineSession: false,
        })
      },

      setUser: (user: User) => {
        set({ user })
      },

      setTokens: (tokens: AuthTokens) => {
        set({ tokens })
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading })
      },

      setInitialized: (isInitialized: boolean) => {
        set({ isInitialized })
      },

      setErpConnections: (connections, selectedId, pendingSelection) => {
        set({
          erpConnections: connections,
          selectedErpConnectionId: selectedId,
          pendingErpSelection: pendingSelection,
        })
      },

      selectErpConnection: (id: number) => {
        set({ selectedErpConnectionId: id, pendingErpSelection: false })
      },

      setErpEmployeeId: (erpConnectionId: number, erpEmployeeId: number | null) => {
        set((state) =>
          state.erpEmployeeIdByConnection[erpConnectionId] === erpEmployeeId
            ? state
            : { erpEmployeeIdByConnection: { ...state.erpEmployeeIdByConnection, [erpConnectionId]: erpEmployeeId } }
        )
      },

      startOfflineSession: ({ user, erpConnections, selectedErpConnectionId, erpEmployeeIdByConnection }: OfflineAuthSnapshot) => {
        set({
          user,
          tokens: null,
          isAuthenticated: true,
          isLoading: false,
          erpConnections,
          selectedErpConnectionId,
          pendingErpSelection: false,
          erpEmployeeIdByConnection: erpEmployeeIdByConnection ?? {},
          offlineSession: true,
        })
      },

      clearAuth: () => {
        set({
          ...initialState,
          isInitialized: true,
        })
      },

      hasPermission: (permission: string): boolean => {
        const { user } = get()
        if (!user) return false
        return user.permissions.includes(permission)
      },

      hasRole: (role: string): boolean => {
        const { user } = get()
        if (!user) return false
        return user.roles.some(
          (r) => r.toLowerCase() === role.toLowerCase()
        )
      },

      hasAnyRole: (roles: string[]): boolean => {
        const { user } = get()
        if (!user) return false
        return roles.some((role) =>
          user.roles.some((r) => r.toLowerCase() === role.toLowerCase())
        )
      },

      hasAllRoles: (roles: string[]): boolean => {
        const { user } = get()
        if (!user) return false
        return roles.every((role) =>
          user.roles.some((r) => r.toLowerCase() === role.toLowerCase())
        )
      },

      hasAnyPermission: (permissions: string[]): boolean => {
        const { user } = get()
        if (!user) return false
        return permissions.some((permission) =>
          user.permissions.includes(permission)
        )
      },

      hasAllPermissions: (permissions: string[]): boolean => {
        const { user } = get()
        if (!user) return false
        return permissions.every((permission) =>
          user.permissions.includes(permission)
        )
      },
    }),
    {
      name: 'c2-auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
        erpConnections: state.erpConnections,
        selectedErpConnectionId: state.selectedErpConnectionId,
        pendingErpSelection: state.pendingErpSelection,
        erpEmployeeIdByConnection: state.erpEmployeeIdByConnection,
        offlineSession: state.offlineSession,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setInitialized(true)
        }
      },
    }
  )
)

// Selector hooks for better performance
export const useUser = () => useAuthStore((state) => state.user)
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated)
export const useAuthLoading = () => useAuthStore((state) => state.isLoading)
export const useAuthInitialized = () => useAuthStore((state) => state.isInitialized)
export const useTokens = () => useAuthStore((state) => state.tokens)

// Helper to get tokens outside React components (for API client)
export const getStoredTokens = (): AuthTokens | null => {
  return useAuthStore.getState().tokens
}

export const setStoredTokens = (tokens: AuthTokens): void => {
  useAuthStore.getState().setTokens(tokens)
}

export const getSelectedErpConnectionId = (): number | null => {
  return useAuthStore.getState().selectedErpConnectionId
}

export const clearStoredAuth = (): void => {
  useAuthStore.getState().clearAuth()
}
